// /home/ec2-user/brand-insights-dashboard/frontend/src/components/image_gallery.js
import React, { useState, useMemo } from 'react';
import {
  Grid, Card, CardMedia, CardContent, CardActions,
  Typography, IconButton, Button, Chip, Box,
  Tooltip, Pagination, Stack, Modal, Fade, Backdrop,
  LinearProgress, Divider, CircularProgress, Paper
} from '@mui/material';
import {
  Favorite, Comment, Share, Visibility,
  OpenInNew, CalendarToday, Close,
  ExpandMore, ExpandLess, LocalFireDepartment,
  Download, Link, BrokenImage, Image as ImageIcon,
  FilterList
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// ========== COMPONENTI STYLIZZATI ==========

const StyledCard = styled(Card)(({ theme, hasimage }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: hasimage === 'true' ? 'translateY(-4px)' : 'none',
    boxShadow: hasimage === 'true' ? theme.shadows[8] : theme.shadows[1],
  },
  border: hasimage === 'true' ? '1px solid' : '2px dashed',
  borderColor: hasimage === 'true' ? theme.palette.divider : theme.palette.warning.light,
}));

const EngagementBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: '0.75rem',
  fontWeight: 'bold',
  zIndex: 2,
}));

const LightboxModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
  },
});

const LightboxContent = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '90vw',
  maxHeight: '90vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  outline: 'none',
}));

// ========== COMPONENTE PRINCIPALE ==========

const DiscoveryGallery = ({ 
  images, 
  pagination, 
  onPageChange,
  stats,  // 🔥 Ricevi statistiche complete dal backend
  loading,
  activeFilters = {},  // 🔥 Ricevi i filtri attivi
  sortBy = 'total_engagement',  // 🔥 Ricevi l'ordinamento
  currentPage,  // 🔥 Ricevi currentPage come prop (calcolato in App.js)
  onSortChange  // 🔥 NUOVO: Handler per cambio ordinamento
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [visibleLabels, setVisibleLabels] = useState({});
  const [failedImages, setFailedImages] = useState(new Set());

  // Handler per apertura/chiusura lightbox
  const handleOpenLightbox = (image) => {
    setSelectedImage(image);
  };

  const handleCloseLightbox = () => {
    setSelectedImage(null);
  };

  // Toggle espansione card
  const handleToggleExpand = (imageId) => {
    setExpandedCards(prev => ({
      ...prev,
      [imageId]: !prev[imageId]
    }));
  };

  // Toggle visibilità etichette
  const handleToggleLabels = (imageId) => {
    setVisibleLabels(prev => ({
      ...prev,
      [imageId]: !prev[imageId]
    }));
  };

  // 🔥 Handler cambio pagina
  const handlePageChange = (event, page) => {
    const newOffset = (page - 1) * pagination.limit;
    onPageChange(newOffset);
  };

  // 🔥 Handler cambio ordinamento
  const handleSortChange = (newSortBy) => {
    if (onSortChange) {
      onSortChange(newSortBy);
    }
  };

  // Calcola engagement rate per singola immagine
  const calculateEngagementRate = (image) => {
    if (!image.views_count || image.views_count === 0) return 0;
    return ((image.total_engagement || 0) / image.views_count) * 100;
  };

  // Formatta numero per visualizzazione
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    const numValue = typeof num === 'number' ? num : parseFloat(num) || 0;
    
    if (numValue >= 1000000) {
      return (numValue / 1000000).toFixed(1) + 'M';
    }
    if (numValue >= 1000) {
      return (numValue / 1000).toFixed(1) + 'K';
    }
    return numValue.toString();
  };

  // Ottieni colore per engagement rate
  const getEngagementColor = (rate) => {
    if (rate >= 10) return '#4caf50'; // Ottimo
    if (rate >= 5) return '#ff9800';  // Buono
    if (rate >= 2) return '#ffeb3b';  // Medio
    return '#f44336';                 // Basso
  };

  // Funzione per estrarre URL immagine o base64
  const getImageData = useMemo(() => (image) => {
    if (!image) return { url: null, isBase64: false, source: null };
    
    const imageKey = image.source_post_id || Math.random().toString();
    if (failedImages.has(imageKey)) {
      return { url: null, isBase64: false, source: 'failed' };
    }
    
    // 🔥 PRIORITÀ 1: image_data (base64 direttamente nel campo)
    if (image.image_data) {
      const base64Data = image.image_data.startsWith('data:') 
        ? image.image_data 
        : `data:image/jpeg;base64,${image.image_data}`;
      
      return { 
        url: base64Data, 
        isBase64: true, 
        source: 'image_data',
        data: { 
          original_size: base64Data.length,
          source_field: 'image_data',
          is_base64: true
        }
      };
    }
    
    // 🔥 PRIORITÀ 2: first_image_base64 (dalla nuova API v2)
    if (image.first_image_base64) {
      const base64Data = image.first_image_base64.startsWith('data:') 
        ? image.first_image_base64 
        : `data:image/jpeg;base64,${image.first_image_base64}`;
      
      return { 
        url: base64Data, 
        isBase64: true, 
        source: 'first_image_base64',
        data: { 
          original_size: base64Data.length,
          source_field: 'first_image_base64',
          is_base64: true
        }
      };
    }
    
    // 🔥 PRIORITÀ 3: image_post (formato legacy, per compatibilità)
    if (image.image_post) {
      try {
        // CASO 1: Array di immagini (esempio Facebook)
        if (Array.isArray(image.image_post) && image.image_post.length > 0) {
          const firstImage = image.image_post[0];
          
          // Prima cerca URL
          if (firstImage.url) {
            return { 
              url: firstImage.url, 
              isBase64: false, 
              source: 'url',
              data: firstImage 
            };
          }
          
          // Poi cerca base64
          if (firstImage.original_base64) {
            const base64Data = firstImage.original_base64.startsWith('data:') 
              ? firstImage.original_base64 
              : `data:image/jpeg;base64,${firstImage.original_base64}`;
            
            return { 
              url: base64Data, 
              isBase64: true, 
              source: 'original_base64',
              data: firstImage 
            };
          }
          
          if (firstImage.resized_base64) {
            const base64Data = firstImage.resized_base64.startsWith('data:') 
              ? firstImage.resized_base64 
              : `data:image/jpeg;base64,${firstImage.resized_base64}`;
            
            return { 
              url: base64Data, 
              isBase64: true, 
              source: 'resized_base64',
              data: firstImage 
            };
          }
        }
        
        // CASO 2: Stringa base64 diretta
        else if (typeof image.image_post === 'string') {
          const base64Data = image.image_post.startsWith('data:') 
            ? image.image_post 
            : `data:image/jpeg;base64,${image.image_post}`;
          
          return { 
            url: base64Data, 
            isBase64: true, 
            source: 'base64_string',
            data: null 
          };
        }
        
        // CASO 3: Oggetto singolo
        else if (typeof image.image_post === 'object' && image.image_post !== null) {
          if (image.image_post.url) {
            return { 
              url: image.image_post.url, 
              isBase64: false, 
              source: 'url_object',
              data: image.image_post 
            };
          }
          
          if (image.image_post.original_base64) {
            const base64Data = image.image_post.original_base64.startsWith('data:') 
              ? image.image_post.original_base64 
              : `data:image/jpeg;base64,${image.image_post.original_base64}`;
            
            return { 
              url: base64Data, 
              isBase64: true, 
              source: 'original_base64_object',
              data: image.image_post 
            };
          }
        }
      } catch (error) {
        console.error('Error parsing image_post:', error, image.image_post);
        return { url: null, isBase64: false, source: 'parse_error' };
      }
    }
    
    // Fallback: post_url o image_url
    if (image.post_url) {
      return { url: image.post_url, isBase64: false, source: 'post_url' };
    }
    
    if (image.image_url) {
      return { url: image.image_url, isBase64: false, source: 'image_url' };
    }
    
    return { url: null, isBase64: false, source: 'no_image' };
  }, [failedImages]);

  // Handler per errori di caricamento immagine
  const handleImageError = (image) => {
    const imageKey = image.source_post_id || Math.random().toString();
    setFailedImages(prev => new Set(prev).add(imageKey));
    console.warn(`❌ Immagine non caricabile: ${imageKey}`);
  };

  // 🔥 KPI BADGES PER HEADER - FORMATO UNIFORME
  const renderKPIBadges = () => {
    if (!stats) return null;

    // Estrai metriche dalle stats
    const totalEngagement = stats.total_engagement || 
                           stats.kpis?.total_engagement || 
                           stats.engagement?.total || 
                           0;
    
    const totalLikes = stats.engagement?.likes || 
                      stats.kpis?.total_likes || 
                      stats.interactions?.total_likes || 
                      0;
    
    const totalComments = stats.engagement?.comments || 
                         stats.kpis?.total_comments || 
                         stats.interactions?.total_comments || 
                         0;
    
    const totalShares = stats.engagement?.shares || 
                       stats.kpis?.total_shares || 
                       stats.interactions?.total_shares || 
                       0;
    
    const totalViews = stats.engagement?.views || 
                      stats.kpis?.total_views || 
                      stats.interactions?.total_views || 
                      0;

    const kpis = [
      {
        id: 'immagini',
        value: pagination.total,
        icon: '🖼️',
        label: 'Immagini',
        color: 'secondary.main',
        show: pagination.total > 0
      },
      {
        id: 'engagement',
        value: totalEngagement,
        icon: '🔥',
        label: 'Interazioni',
        color: '#ff5722',
        show: totalEngagement > 0
      },
      {
        id: 'likes',
        value: totalLikes,
        icon: '👍',
        label: 'Likes',
        color: '#e91e63',
        show: totalLikes > 0
      },
      {
        id: 'comments',
        value: totalComments,
        icon: '💬',
        label: 'Comments',
        color: '#2196f3',
        show: totalComments > 0
      },
      {
        id: 'shares',
        value: totalShares,
        icon: '🔄',
        label: 'Shares',
        color: '#4caf50',
        show: totalShares > 0
      },
      {
        id: 'views',
        value: totalViews,
        icon: '👁️',
        label: 'Views',
        color: '#ff9800',
        show: totalViews > 0
      }
    ];

    // Filtra solo i KPI da mostrare
    const kpisToShow = kpis.filter(kpi => kpi.show);

    if (kpisToShow.length === 0) return null;

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        flexWrap: 'wrap', 
        gap: 1
      }}>
        {/* Mostra ogni KPI come badge */}
        {kpisToShow.map((kpi) => (
          <Box
            key={kpi.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              backgroundColor: kpi.id === 'immagini' ? 'rgba(156, 39, 176, 0.08)' : 'rgba(25, 118, 210, 0.08)',
              border: '1px solid',
              borderColor: kpi.id === 'immagini' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(25, 118, 210, 0.2)',
              minWidth: 'auto',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: kpi.id === 'immagini' ? 'rgba(156, 39, 176, 0.12)' : 'rgba(25, 118, 210, 0.12)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }
            }}
          >
            {/* Icona */}
            <Typography variant="body2" sx={{ 
              fontSize: '0.9rem',
              lineHeight: 1,
              opacity: 0.9
            }}>
              {kpi.icon}
            </Typography>
            
            {/* Numero */}
            <Typography variant="body2" sx={{ 
              fontWeight: 'bold',
              fontSize: '0.85rem',
              color: kpi.id === 'immagini' ? 'secondary.main' : 'primary.main',
              minWidth: 'fit-content'
            }}>
              {formatNumber(kpi.value)}
            </Typography>
            
            {/* Label - TESTO COMPLETO */}
            <Typography variant="caption" sx={{ 
              fontSize: '0.75rem',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              fontWeight: 500
            }}>
              {kpi.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  
  // 🔥 ETICHETTE PRINCIPALI PER HEADER - FORMATO UNIFORME (adattato alla lunghezza)
  const renderMainLabels = () => {
    if (!stats) return null;

    // 🔥 Estrai le etichette principali dalle statistiche
    const topValues = stats?.top_values || 
                    stats?.top_labels || 
                    stats?.kpis?.top_labels || 
                    {};

    if (!topValues || Object.keys(topValues).length === 0) {
      return null;
    }

    // Mappa come nelle immagini
    const fieldMap = [
      { field: 'category', icon: '🏷️', label: 'Category' },
      { field: 'content_type', icon: '📄', label: 'Content Type' },
      { field: 'primary_value', icon: '💰', label: 'Primary Value' },
      { field: 'visual_style', icon: '🎨', label: 'Visual Style' },
      { field: 'setting', icon: '🏞️', label: 'Setting' },
      { field: 'target_implied', icon: '🎯', label: 'Target' },
      { field: 'cta_type', icon: '📢', label: 'CTA' },
      { field: 'sentiment', icon: '😊', label: 'Sentiment' },
      { field: 'intent', icon: '🎯', label: 'Intent' }
    ];

    // Filtra solo i campi che hanno valori validi
    const validLabels = fieldMap.filter(item => {
      const topValue = topValues[item.field];
      return topValue && 
            topValue.value && 
            topValue.value !== 'N/A' && 
            topValue.value !== 'Unknown' &&
            topValue.value !== '';
    });

    if (validLabels.length === 0) {
      return null;
    }

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        flexWrap: 'wrap', 
        gap: 1,
        justifyContent: 'flex-start' // Allineamento a sinistra
      }}>
        {/* Label "Top labels:" con icona */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            borderRadius: '20px',
            backgroundColor: 'rgba(121, 85, 72, 0.08)',
            border: '1px solid',
            borderColor: 'rgba(121, 85, 72, 0.2)',
            minWidth: 'auto',
            height: '32px'
          }}
        >
          <Typography variant="body2" sx={{ 
            fontSize: '0.9rem',
            lineHeight: 1,
            opacity: 0.9
          }}>
            🏷️
          </Typography>
          
          <Typography variant="caption" sx={{ 
            fontSize: '0.75rem',
            color: 'text.secondary',
            fontWeight: 500,
            whiteSpace: 'nowrap'
          }}>
            Top labels:
          </Typography>
        </Box>
        
        {/* Badge delle etichette - adattati alla lunghezza del testo */}
        {validLabels.map((item) => (
          <Box
            key={item.field}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.2,
              py: 0.5,
              borderRadius: '20px',
              backgroundColor: 'rgba(76, 175, 80, 0.08)',
              border: '1px solid',
              borderColor: 'rgba(76, 175, 80, 0.2)',
              minWidth: 'auto',
              maxWidth: '200px',
              height: '32px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.12)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }
            }}
          >
            {/* Icona */}
            <Typography variant="body2" sx={{ 
              fontSize: '0.8rem', 
              opacity: 0.8,
              lineHeight: 1,
              flexShrink: 0
            }}>
              {item.icon}
            </Typography>
            
            {/* Valore - testo adattabile */}
            <Typography variant="caption" sx={{ 
              fontSize: '0.75rem',
              color: 'text.secondary',
              fontWeight: 500,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '150px'
            }}>
              {topValues[item.field].value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // 🔥 FILTRI ATTIVI PER HEADER
  const renderActiveFiltersPill = () => {
    if (!activeFilters || Object.keys(activeFilters).length === 0) {
      return null;
    }

    // Conta i filtri attivi (esclusi quelli vuoti o di sistema)
    const activeFilterCount = Object.entries(activeFilters).filter(([key, value]) => {
      if (!value) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (key === 'min_engagement' && value === 0) return false;
      if (key === 'sort_by' || key === 'sort_order' || key === 'limit' || key === 'offset') return false;
      return true;
    }).length;

    if (activeFilterCount === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.2,
          py: 0.4,
          borderRadius: '16px',
          backgroundColor: 'rgba(156, 39, 176, 0.08)',
          border: '1px solid',
          borderColor: 'rgba(156, 39, 176, 0.2)'
        }}
      >
        <FilterList fontSize="small" sx={{ fontSize: '0.8rem', color: 'secondary.main' }} />
        <Typography variant="caption" sx={{ 
          fontSize: '0.75rem',
          color: 'secondary.main',
          fontWeight: 500
        }}>
          {activeFilterCount} filtri
        </Typography>
      </Box>
    );
  };

  // Renderizza etichette dell'immagine
  const renderImageLabels = (image) => {
    const labels = [
      { field: 'category', label: 'Categoria', icon: '🏷️' },
      { field: 'content_type', label: 'Tipo Contenuto', icon: '📄' },
      { field: 'primary_value', label: 'Valore Primario', icon: '💰' },
      { field: 'visual_style', label: 'Stile Visuale', icon: '🎨' },
      { field: 'setting', label: 'Setting', icon: '🏞️' },
      { field: 'target_implied', label: 'Target', icon: '🎯' },
      { field: 'cta_type', label: 'CTA', icon: '📢' },
      { field: 'sentiment', label: 'Sentiment', icon: '😊' },
      { field: 'intent', label: 'Intent', icon: '🎯' }
    ];

    const validLabels = labels.filter(item => image[item.field]);

    if (validLabels.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          🏷️ Etichette ({validLabels.length})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {validLabels.map((item, index) => (
            <Tooltip key={index} title={item.label}>
              <Chip
                label={`${item.icon} ${image[item.field]}`}
                size="small"
                variant="outlined"
                sx={{ 
                  fontSize: '0.7rem',
                  height: 24,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Box>
    );
  };

  // Renderizza metriche engagement
  const renderEngagementMetrics = (image) => {
    const engagementRate = calculateEngagementRate(image);
    
    return (
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={3} justifyContent="space-between">
          <Tooltip title="Likes">
            <Box sx={{ textAlign: 'center' }}>
              <Favorite fontSize="small" sx={{ color: '#e91e63', mb: 0.5 }} />
              <Typography variant="caption" fontWeight="bold">
                {formatNumber(image.likes_count || 0)}
              </Typography>
            </Box>
          </Tooltip>
          
          <Tooltip title="Comments">
            <Box sx={{ textAlign: 'center' }}>
              <Comment fontSize="small" sx={{ color: '#2196f3', mb: 0.5 }} />
              <Typography variant="caption" fontWeight="bold">
                {formatNumber(image.comments_count || 0)}
              </Typography>
            </Box>
          </Tooltip>
          
          <Tooltip title="Shares">
            <Box sx={{ textAlign: 'center' }}>
              <Share fontSize="small" sx={{ color: '#4caf50', mb: 0.5 }} />
              <Typography variant="caption" fontWeight="bold">
                {formatNumber(image.shares_count || 0)}
              </Typography>
            </Box>
          </Tooltip>
          
          <Tooltip title="Views">
            <Box sx={{ textAlign: 'center' }}>
              <Visibility fontSize="small" sx={{ color: '#ff9800', mb: 0.5 }} />
              <Typography variant="caption" fontWeight="bold">
                {formatNumber(image.views_count || 0)}
              </Typography>
            </Box>
          </Tooltip>
        </Stack>
        
        {image.views_count > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Engagement Rate
              </Typography>
              <Typography 
                variant="caption" 
                fontWeight="bold"
                sx={{ color: getEngagementColor(engagementRate) }}
              >
                {engagementRate.toFixed(2)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(engagementRate * 2, 100)}
              sx={{ 
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getEngagementColor(engagementRate),
                }
              }}
            />
          </Box>
        )}
        
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalFireDepartment fontSize="small" sx={{ color: '#ff5722' }} />
            <Typography variant="caption" fontWeight="bold">
              {formatNumber(image.total_engagement || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              total
            </Typography>
          </Box>
          
          {image.posted_at && (
            <Tooltip title="Data pubblicazione">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarToday fontSize="small" sx={{ color: '#9c27b0' }} />
                <Typography variant="caption">
                  {new Date(image.posted_at).toLocaleDateString('it-IT')}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  // Renderizza informazioni immagine
  const renderImageInfo = (imageData) => {
    if (!imageData || !imageData.data) return null;
    
    const info = [];
    if (imageData.data.original_size) {
      info.push(`${Math.round(imageData.data.original_size / 1024)}KB`);
    }
    if (imageData.data.source_field) {
      info.push(imageData.data.source_field);
    }
    if (imageData.data.is_video_thumbnail) {
      info.push('Video Thumbnail');
    }
    
    if (info.length === 0) return null;
    
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
        {info.join(' • ')}
      </Typography>
    );
  };

  // Renderizza singola card immagine
  const renderImageCard = (image, index) => {
    const imageId = image.source_post_id || index;
    const isExpanded = expandedCards[imageId];
    const showLabels = visibleLabels[imageId] !== false;
    const imageData = getImageData(image);
    const hasValidImage = imageData.url !== null;
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={imageId}>
        <StyledCard 
          hasimage={hasValidImage ? "true" : "false"}
        >
          {/* Thumbnail con engagement badge */}
          <Box sx={{ 
            position: 'relative', 
            height: 200, 
            overflow: 'hidden',
            bgcolor: hasValidImage ? 'transparent' : 'grey.50'
          }}>
            {hasValidImage ? (
              <>
                <CardMedia
                  component="img"
                  height="200"
                  image={imageData.url}
                  alt={image.post_text || 'Social media image'}
                  sx={{ 
                    cursor: 'pointer',
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                    '&:hover': {
                      opacity: 0.9,
                      transform: 'scale(1.05)',
                      transition: 'transform 0.3s ease'
                    }
                  }}
                  onClick={() => handleOpenLightbox(image)}
                  onError={() => handleImageError(image)}
                />
                {/* 🔥 RIMOSSO: Badge "BASE64" */}
              </>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  p: 2
                }}
                onClick={() => handleOpenLightbox(image)}
              >
                <BrokenImage sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                <Typography variant="caption" color="text.secondary" align="center">
                  Immagine non disponibile
                </Typography>
                {image.image_post && (
                  <Typography variant="caption" color="text.secondary" align="center" sx={{ fontSize: '0.6rem', mt: 0.5 }}>
                    {Array.isArray(image.image_post) 
                      ? `${image.image_post.length} elementi`
                      : 'Dati immagine presenti'
                    }
                  </Typography>
                )}
              </Box>
            )}
            
            <EngagementBadge>
              <LocalFireDepartment fontSize="small" />
              <span>{formatNumber(image.total_engagement || 0)}</span>
            </EngagementBadge>
            
            {/* Ranking badge */}
            <Box sx={{
              position: 'absolute',
              top: 8,
              left: 8, // 🔥 Modificato: ora è sempre a sinistra 8px
              backgroundColor: hasValidImage ? 'rgba(25, 118, 210, 0.9)' : 'rgba(158, 158, 158, 0.9)',
              color: 'white',
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              zIndex: 1
            }}>
              {index + 1 + pagination.offset}
            </Box>
          </Box>

          <CardContent sx={{ flexGrow: 1, pb: 1 }}>
            {/* Brand e Piattaforma */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                {image.brand || 'N/A'}
              </Typography>
              <Chip 
                label={image.platform || 'N/A'} 
                size="small" 
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            </Box>

            {/* Tipo di post */}
            <Chip 
              label={image.post_type || 'Post'} 
              size="small" 
              sx={{ 
                mb: 1.5,
                backgroundColor: hasValidImage ? 'primary.light' : 'grey.300',
                color: hasValidImage ? 'primary.contrastText' : 'text.secondary',
                fontSize: '0.7rem',
                height: 22
              }}
            />

            {/* Informazioni immagine */}
            {hasValidImage && renderImageInfo(imageData)}

            {/* Testo del post (truncato/espandibile) */}
            {image.post_text && (
              <Box sx={{ mb: 1.5 }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 'unset' : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4,
                    fontSize: '0.85rem'
                  }}
                >
                  {image.post_text}
                </Typography>
                {image.post_text.length > 100 && (
                  <Button 
                    size="small" 
                    onClick={() => handleToggleExpand(imageId)}
                    sx={{ mt: 0.5, p: 0, minWidth: 'auto', fontSize: '0.75rem' }}
                  >
                    {isExpanded ? (
                      <>
                        <ExpandLess fontSize="small" sx={{ mr: 0.5 }} />
                        Mostra meno
                      </>
                    ) : (
                      <>
                        <ExpandMore fontSize="small" sx={{ mr: 0.5 }} />
                        Mostra di più
                      </>
                    )}
                  </Button>
                )}
              </Box>
            )}

            {/* Etichette immagine (toggle) */}
            {showLabels && renderImageLabels(image)}
            
            {/* Toggle etichette */}
            {Object.keys(image).some(key => 
              ['category', 'content_type', 'primary_value', 'visual_style', 
               'setting', 'target_implied', 'cta_type', 'sentiment', 'intent'].includes(key) && image[key]
            ) && (
              <Button 
                size="small" 
                onClick={() => handleToggleLabels(imageId)}
                sx={{ mt: 1, p: 0, minWidth: 'auto', fontSize: '0.7rem' }}
              >
                {showLabels ? 'Nascondi etichette' : 'Mostra etichette'}
              </Button>
            )}

            {/* Metriche engagement */}
            <Divider sx={{ my: 1.5 }} />
            {renderEngagementMetrics(image)}
          </CardContent>

          <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Tooltip title="Apri post originale">
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const url = image.post_url || image.image_url;
                    if (url) {
                      window.open(url, '_blank');
                    }
                  }}
                  sx={{ flex: 1 }}
                  disabled={!image.post_url && !image.image_url}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={hasValidImage ? "Scarica immagine" : "Immagine non disponibile"}>
                <span>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      if (hasValidImage) {
                        if (imageData.isBase64) {
                          // Per base64, crea un link di download
                          const link = document.createElement('a');
                          link.href = imageData.url;
                          link.download = `image_${image.source_post_id || 'social'}.jpg`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } else {
                          window.open(imageData.url, '_blank');
                        }
                      }
                    }}
                    sx={{ flex: 1 }}
                    disabled={!hasValidImage}
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              
              <Tooltip title="Copia link immagine">
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const url = image.post_url || image.image_url || imageData.url;
                    if (url) {
                      navigator.clipboard.writeText(url);
                      alert('Link copiato negli appunti');
                    }
                  }}
                  sx={{ flex: 1 }}
                  disabled={!image.post_url && !image.image_url && !hasValidImage}
                >
                  <Link fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardActions>
        </StyledCard>
      </Grid>
    );
  };

  // Renderizza lightbox
  const renderLightbox = () => {
    if (!selectedImage) return null;

    const imageData = getImageData(selectedImage);
    const hasValidImage = imageData.url !== null;

    return (
      <LightboxModal
        open={!!selectedImage}
        onClose={handleCloseLightbox}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={!!selectedImage}>
          <LightboxContent>
            {/* Toolbar lightbox */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 10
            }}>
              <Typography variant="h6" color="white">
                {selectedImage.brand || 'N/A'} • {selectedImage.platform || 'N/A'}
                {/* 🔥 RIMOSSO: Chip "BASE64" */}
              </Typography>
              
              <IconButton onClick={handleCloseLightbox} sx={{ color: 'white' }}>
                <Close />
              </IconButton>
            </Box>

            {/* Immagine */}
            <Box sx={{ 
              maxHeight: 'calc(90vh - 120px)', 
              overflow: 'auto', 
              p: 4,
              mt: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              {hasValidImage ? (
                <>
                  <img
                    src={imageData.url}
                    alt={selectedImage.post_text || 'Social media image'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60vh',
                      objectFit: 'contain',
                      borderRadius: 8,
                      display: 'block'
                    }}
                    onError={() => handleImageError(selectedImage)}
                  />
                  
                  {/* Informazioni tecniche immagine */}
                  {imageData.data && (
                    <Box sx={{ 
                      mt: 2, 
                      p: 1.5, 
                      bgcolor: 'rgba(0,0,0,0.1)', 
                      borderRadius: 1,
                      width: '100%',
                      maxWidth: 800
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon fontSize="small" />
                        Dati tecnici immagine:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                        {imageData.data.original_size && (
                          <Typography variant="caption">
                            Dimensione: {Math.round(imageData.data.original_size / 1024)}KB
                          </Typography>
                        )}
                        {imageData.data.source_field && (
                          <Typography variant="caption">
                            Fonte: {imageData.data.source_field}
                          </Typography>
                        )}
                        {imageData.data.is_video_thumbnail !== undefined && (
                          <Typography variant="caption">
                            {imageData.data.is_video_thumbnail ? 'Thumbnail video' : 'Immagine'}
                          </Typography>
                        )}
                        {/* 🔥 RIMOSSO: "Formato: Base64" */}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  color: 'white',
                  py: 8 
                }}>
                  <BrokenImage sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">
                    Immagine non disponibile
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                    I dati dell'immagine potrebbero essere corrotti o in formato non supportato
                  </Typography>
                </Box>
              )}
              
              {/* Info immagine */}
              <Box sx={{ 
                mt: 3, 
                color: 'white', 
                p: 3, 
                backgroundColor: 'rgba(0, 0, 0, 0.7)', 
                borderRadius: 2,
                width: '100%',
                maxWidth: 800
              }}>
                <Typography variant="h6" gutterBottom>
                  Dettagli Post
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Brand:</strong> {selectedImage.brand || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Piattaforma:</strong> {selectedImage.platform || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tipo Post:</strong> {selectedImage.post_type || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>ID Post:</strong> {selectedImage.source_post_id?.substring(0, 20)}...
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Data:</strong> {selectedImage.posted_at ? 
                        new Date(selectedImage.posted_at).toLocaleString('it-IT') : 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Engagement Totale:</strong> {formatNumber(selectedImage.total_engagement)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Engagement Rate:</strong> {calculateEngagementRate(selectedImage).toFixed(2)}%
                    </Typography>
                    <Typography variant="body2">
                      <strong>Views:</strong> {formatNumber(selectedImage.views_count || 0)}
                    </Typography>
                  </Grid>
                </Grid>
                
                {selectedImage.post_text && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Testo:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                      {selectedImage.post_text}
                    </Typography>
                  </Box>
                )}
                
                {/* Etichette */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Etichette:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {renderImageLabels(selectedImage)}
                  </Box>
                </Box>
              </Box>
            </Box>
          </LightboxContent>
        </Fade>
      </LightboxModal>
    );
  };

  // 🔥 Loading state
  if (loading && images.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          Caricamento immagini...
        </Typography>
      </Box>
    );
  }

  // 🔥 Calcola totalPages usando i dati da pagination
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Box sx={{ width: '100%' }}>
      {/* 🔥 RIMOSSO: Box "Metriche Principali" */}
      
      {/* Grid immagini */}
      {images.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nessuna immagine trovata
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prova a modificare i filtri o a cercare con criteri diversi
          </Typography>
        </Box>
      ) : (
        <>
          {/* 🔥 HEADER UNIFICATO - 3 RIGHE SEPARATE */}
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1.5,
            bgcolor: 'background.paper'
          }}>
            {/* 🔥 RIGA 1: Titolo + KPI badges */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexWrap: 'wrap', 
              gap: 2 
            }}>
              {/* Titolo */}
              <Typography variant="h6" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                🖼️ Discovery Gallery
              </Typography>
              
              {/* 🔥 KPI BADGES - FORMATO UNIFORME */}
              {renderKPIBadges()}
            </Box>
            
            {/* 🔥 RIGA 2: Top labels + etichette */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexWrap: 'wrap', 
              gap: 1,
              justifyContent: 'flex-start' // Allineamento a sinistra
            }}>
              {renderMainLabels()}
            </Box>
            
            {/* 🔥 RIGA 3: Ordinamento */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexWrap: 'wrap', 
              gap: 2,
              justifyContent: 'space-between',
              pt: 0.5
            }}>
              {/* Ordinamento - allineato a sinistra */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.2,
                    py: 0.5,
                    borderRadius: '20px',
                    backgroundColor: 'rgba(96, 125, 139, 0.08)',
                    border: '1px solid',
                    borderColor: 'rgba(96, 125, 139, 0.2)',
                    height: '32px'
                  }}
                >
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}>
                    Ordina per:
                  </Typography>
                </Box>
                
                {/* Chip ordinamento */}
                <Chip
                  label="Engagement"
                  size="small"
                  variant={sortBy === 'total_engagement' ? "filled" : "outlined"}
                  color="primary"
                  onClick={() => handleSortChange('total_engagement')}
                  sx={{ 
                    height: '32px',
                    '& .MuiChip-label': { 
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }
                  }}
                />
                <Chip
                  label="Data"
                  size="small"
                  variant={sortBy === 'posted_at' ? "filled" : "outlined"}
                  color="primary"
                  onClick={() => handleSortChange('posted_at')}
                  sx={{ 
                    height: '32px',
                    '& .MuiChip-label': { 
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
              
              {/* Spazio vuoto per bilanciare il layout */}
              <Box sx={{ flex: 1 }}></Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {images.map((image, index) => renderImageCard(image, index))}
          </Grid>
        </>
      )}

      {/* 🔥 PAGINAZIONE */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.95rem',
              }
            }}
          />
        </Box>
      )}

      {/* Lightbox */}
      {renderLightbox()}
    </Box>
  );
};

export default DiscoveryGallery;